import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import DownloadIcon from "@mui/icons-material/Download";
import { getPivotProductStores } from "../../services/pivot";
import {
  PivotProductStoresParams,
  ProductStoresResponse,
} from "../../types/pivot.type";
import { PivotDetailRow, PivotNoteRow } from "./PivotGroupedTable";
import { CodeChip, fmtQty } from "./pivotFormat";
import { PV } from "./pivotTokens";
import { channelsForProductRow } from "./pivotDrill";

type Entry =
  | { status: "loading" }
  | { status: "ok"; data: ProductStoresResponse }
  | { status: "error" };

export type ProductStoresBaseParams = Omit<PivotProductStoresParams, "product_code">;

const expandKey = (groupKey: string, productCode: string) => `${groupKey}|${productCode}`;

/**
 * State for the product row's inline drill (board B2): which rows are open, one lazily
 * fetched `/pivot/products/stores` answer per product, and which channels inside an open
 * product are folded.
 *
 * The answer is per product, not per (product, channel group): the same product open under
 * two channel groups reads the same response and each shows its own channel's slice of it.
 * Everything is dropped when the scope moves — a cached tree for another date range would
 * sit under the new range's product row with nothing to say it is stale.
 */
export function useProductStoresDrill(base: ProductStoresBaseParams) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [entries, setEntries] = useState<Record<string, Entry>>({});
  const [closedChannels, setClosedChannels] = useState<Set<string>>(new Set());
  const entriesRef = useRef(entries);
  entriesRef.current = entries;
  // Bumped on every scope change; a response from an older generation is discarded.
  const generation = useRef(0);

  useEffect(() => {
    generation.current += 1;
    setExpanded(new Set());
    setEntries({});
    setClosedChannels(new Set());
  }, [base]);

  const load = useCallback(
    (productCode: string) => {
      const gen = generation.current;
      setEntries((e) => ({ ...e, [productCode]: { status: "loading" } }));
      (async () => {
        try {
          const { data } = await getPivotProductStores({ ...base, product_code: productCode });
          const payload = data.data;
          if (gen === generation.current)
            setEntries((e) => ({
              ...e,
              [productCode]: payload ? { status: "ok", data: payload } : { status: "error" },
            }));
        } catch {
          // The shared interceptor already toasted the reason. Mark it so the rows say so and
          // the next click on the product retries.
          if (gen === generation.current)
            setEntries((e) => ({ ...e, [productCode]: { status: "error" } }));
        }
      })();
    },
    [base],
  );

  const expandedRef = useRef(expanded);
  expandedRef.current = expanded;

  const toggle = useCallback(
    (groupKey: string, productCode: string) => {
      const key = expandKey(groupKey, productCode);
      const opening = !expandedRef.current.has(key);
      setExpanded((prev) => {
        const next = new Set(prev);
        if (opening) next.add(key);
        else next.delete(key);
        return next;
      });
      const entry = entriesRef.current[productCode];
      if (opening && (!entry || entry.status === "error")) load(productCode);
    },
    [load],
  );

  const toggleChannel = useCallback((key: string) => {
    setClosedChannels((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  return {
    isExpanded: (groupKey: string, productCode: string) =>
      expanded.has(expandKey(groupKey, productCode)),
    entry: (productCode: string): Entry | undefined => entries[productCode],
    toggle,
    closedChannels,
    toggleChannel,
  };
}

const INDENT_CHANNEL = 40;
const INDENT_STORE = 66;

export function ProductStoresRows({
  entry,
  productCode,
  groupKey,
  isFlat,
  closedChannels,
  onToggleChannel,
  onExport,
  exporting,
}: {
  entry: Entry | undefined;
  productCode: string;
  groupKey: string;
  isFlat: boolean;
  closedChannels: Set<string>;
  onToggleChannel: (key: string) => void;
  onExport: () => void;
  exporting: boolean;
}) {
  const channels =
    entry?.status === "ok" ? channelsForProductRow(entry.data.groups, groupKey, isFlat) : [];
  const storeCount = channels.reduce((n, g) => n + g.row_count, 0);

  return (
    <>
      <PivotNoteRow indent={INDENT_CHANNEL}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <Box component="span" sx={{ color: PV.indigoDark, fontWeight: 600 }}>
            รายชื่อร้านแสดงเฉพาะร้านที่ขายได้ (ขาย &gt; 0)
          </Box>
          {entry?.status === "ok" && (
            <Box component="span" sx={{ color: PV.tx3 }}>
              {isFlat ? "ทุกช่องทาง" : "เฉพาะช่องทางของกลุ่มนี้"} · {fmtQty(storeCount)} ร้าน
            </Box>
          )}
          <Box
            component="button"
            type="button"
            onClick={onExport}
            disabled={exporting}
            sx={{
              ml: "auto",
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              font: "inherit",
              fontSize: 12.5,
              color: PV.indigo,
              bgcolor: "transparent",
              border: "1px solid",
              borderColor: PV.indigoBorder,
              borderRadius: PV.radiusSm,
              px: 1.25,
              py: 0.4,
              cursor: exporting ? "default" : "pointer",
              opacity: exporting ? 0.6 : 1,
              "&:hover": { bgcolor: PV.indigoTint },
            }}
          >
            {exporting ? (
              <CircularProgress size={12} color="inherit" />
            ) : (
              <DownloadIcon sx={{ fontSize: 14 }} />
            )}
            CSV ร้านที่ขายสินค้า {productCode}
          </Box>
        </Box>
      </PivotNoteRow>

      {(!entry || entry.status === "loading") &&
        [0, 1, 2].map((i) => (
          <PivotDetailRow key={i} indent={INDENT_STORE} identity={[null, null]} metrics={null} />
        ))}

      {entry?.status === "error" && (
        <PivotNoteRow indent={INDENT_CHANNEL}>
          <Box component="span" sx={{ color: PV.waste }}>
            โหลดรายชื่อร้านไม่สำเร็จ · ปิดแล้วเปิดแถวสินค้าอีกครั้งเพื่อลองใหม่
          </Box>
        </PivotNoteRow>
      )}

      {entry?.status === "ok" && channels.length === 0 && (
        <PivotNoteRow indent={INDENT_CHANNEL}>
          <Box component="span" sx={{ color: PV.tx3 }}>
            ไม่มีร้านที่ขายสินค้านี้ได้ในช่องทางนี้ในช่วงที่เลือก
          </Box>
        </PivotNoteRow>
      )}

      {channels.map((g) => {
        const channelKey = `${groupKey}|${productCode}|${g.group.key}`;
        const open = !closedChannels.has(channelKey);
        return (
          <Fragment key={g.group.key}>
            <PivotDetailRow
              indent={INDENT_CHANNEL}
              strong
              caret={open}
              onClick={() => onToggleChannel(channelKey)}
              actionLabel={`${open ? "ย่อ" : "ขยาย"}ช่องทาง ${g.group.label ?? g.group.key}`}
              identity={[
                <Box component="span" sx={{ color: PV.tx2, fontWeight: 500 }}>
                  ช่องทาง
                </Box>,
                <>
                  {g.group.label ?? (g.group.key === "__NONE__" ? "ไม่ระบุช่องทาง" : g.group.key)}
                  <Box component="span" sx={{ color: PV.tx3, fontWeight: 400, ml: 1 }}>
                    {fmtQty(g.row_count)} ร้าน
                  </Box>
                </>,
              ]}
              metrics={g.totals}
            />
            {open &&
              g.rows.map((r) => (
                <PivotDetailRow
                  key={r.customer_code}
                  indent={INDENT_STORE}
                  identity={[
                    <CodeChip>{r.customer_code}</CodeChip>,
                    <>
                      {r.customer_name}
                      <Box component="span" sx={{ color: PV.tx3, ml: 0.75 }}>
                        · {r.route_code}
                      </Box>
                    </>,
                  ]}
                  metrics={r.metrics}
                />
              ))}
            {open && g.truncated_row_count > 0 && (
              <PivotNoteRow indent={INDENT_STORE}>
                <Box component="span" sx={{ color: PV.indigo, fontStyle: "italic" }}>
                  แสดงอีก {fmtQty(g.truncated_row_count)} ร้าน · ดาวน์โหลด CSV ของสินค้านี้เพื่อดูครบ
                </Box>
              </PivotNoteRow>
            )}
          </Fragment>
        );
      })}
    </>
  );
}
